import * as React from 'react';
import { useAuthorization } from '../hooks/use-authorization';
import { useSettings, type UpdateSettingsInput, SettingsProvider } from '../state/settings-state';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Spinner } from '../components/ui/spinner';

/**
 * Settings Page — System_Admin only.
 * Manage system configuration: role mapping, SIEM integration, API key policy.
 */

// ─── Lifetime Options ────────────────────────────────────────────────────────

const LIFETIME_OPTIONS = [
  { label: '1 hour', value: 3_600_000 },
  { label: '6 hours', value: 21_600_000 },
  { label: '12 hours', value: 43_200_000 },
  { label: '24 hours', value: 86_400_000 },
  { label: '3 days', value: 259_200_000 },
  { label: '7 days', value: 604_800_000 },
  { label: '30 days', value: 2_592_000_000 },
  { label: '90 days', value: 7_776_000_000 },
  { label: '180 days', value: 15_552_000_000 },
  { label: '1 year', value: 31_536_000_000 },
] as const;

/** Max-lifetime value meaning unlimited, which also allows keys that never expire. */
const UNLIMITED_LIFETIME_MS = 0;

// ─── Inner Component ─────────────────────────────────────────────────────────

function SettingsPageInner(): React.JSX.Element {
  const { isSystemAdmin } = useAuthorization();
  const { state, loadSettings, updateSettings } = useSettings();

  // Form state
  const [systemAdminGroups, setSystemAdminGroups] = React.useState('');
  const [domainAdminGroups, setDomainAdminGroups] = React.useState('');
  const [userGroups, setUserGroups] = React.useState('');
  const [blumiraEndpoint, setBlumiraEndpoint] = React.useState('');
  const [blumiraApiKeySecretArn, setBlumiraApiKeySecretArn] = React.useState('');
  const [maxLifetimeMs, setMaxLifetimeMs] = React.useState(604_800_000);
  const [defaultLifetimeMs, setDefaultLifetimeMs] = React.useState(86_400_000);
  const [maxKeysPerSubdomain, setMaxKeysPerSubdomain] = React.useState(10);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isSystemAdmin) {
      loadSettings();
    }
  }, [isSystemAdmin, loadSettings]);

  // Sync form state when settings load
  React.useEffect(() => {
    if (state.settings) {
      setSystemAdminGroups(state.settings.groupRoleMap.systemAdmin.join(', '));
      setDomainAdminGroups(state.settings.groupRoleMap.domainAdmin.join(', '));
      setUserGroups(state.settings.groupRoleMap.user.join(', '));
      setBlumiraEndpoint(state.settings.blumiraEndpoint);
      setBlumiraApiKeySecretArn(state.settings.blumiraApiKeySecretArn);
      setMaxLifetimeMs(state.settings.apiKeyPolicy.maxLifetimeMs);
      setDefaultLifetimeMs(state.settings.apiKeyPolicy.defaultLifetimeMs);
      setMaxKeysPerSubdomain(state.settings.apiKeyPolicy.maxKeysPerSubdomain);
    }
  }, [state.settings]);

  if (!isSystemAdmin) {
    return (
      <section aria-labelledby="settings-heading">
        <h2 id="settings-heading" className="sr-only">Settings</h2>
        <Alert variant="destructive">
          <AlertTitle>Authorization Error</AlertTitle>
          <AlertDescription>
            You do not have permission to access system settings.
            This feature is restricted to System Administrators.
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  const parseGroupIds = (value: string): string[] =>
    value.split(',').map((s) => s.trim()).filter(Boolean);

  const handleSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setValidationError(null);

    const parsedSystemAdmin = parseGroupIds(systemAdminGroups);
    const parsedDomainAdmin = parseGroupIds(domainAdminGroups);
    const parsedUser = parseGroupIds(userGroups);

    // Client-side validation
    if (parsedSystemAdmin.length === 0) {
      setValidationError('At least one System Admin group ID is required.');
      return;
    }

    if (maxLifetimeMs !== UNLIMITED_LIFETIME_MS && defaultLifetimeMs > maxLifetimeMs) {
      setValidationError('Default lifetime cannot exceed max lifetime.');
      return;
    }

    if (maxKeysPerSubdomain < 1) {
      setValidationError('Max keys per subdomain must be at least 1.');
      return;
    }

    const input: UpdateSettingsInput = {
      groupRoleMap: {
        systemAdmin: parsedSystemAdmin,
        domainAdmin: parsedDomainAdmin,
        user: parsedUser,
      },
      blumiraEndpoint,
      blumiraApiKeySecretArn,
      apiKeyPolicy: {
        maxLifetimeMs,
        defaultLifetimeMs,
        maxKeysPerSubdomain,
      },
    };

    await updateSettings(input);
  };

  if (state.isLoading) {
    return (
      <section aria-labelledby="settings-heading" aria-busy="true">
        <h2 id="settings-heading" className="sr-only">Settings</h2>
        <Spinner label="Loading settings..." />
      </section>
    );
  }

  return (
    <section aria-labelledby="settings-heading">
      <div className="mb-6">
        <h2 id="settings-heading" className="text-2xl font-semibold text-foreground">
          System Settings
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage system configuration for DelegatedDNS.
        </p>
      </div>

      {state.error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
      )}

      {validationError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Role Mapping Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Role Mapping</CardTitle>
            <CardDescription>
              Map EntraID group Object IDs to system roles. Enter comma-separated group IDs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="settings-system-admin-groups" className="mb-1 block text-sm font-medium">
                System Admin Groups
              </label>
              <Input
                id="settings-system-admin-groups"
                value={systemAdminGroups}
                onChange={(e) => setSystemAdminGroups(e.target.value)}
                placeholder="group-object-id-1, group-object-id-2"
                aria-describedby="settings-system-admin-help"
                required
              />
              <p id="settings-system-admin-help" className="mt-1 text-xs text-muted-foreground">
                At least one group ID is required.
              </p>
            </div>
            <div>
              <label htmlFor="settings-domain-admin-groups" className="mb-1 block text-sm font-medium">
                Domain Admin Groups
              </label>
              <Input
                id="settings-domain-admin-groups"
                value={domainAdminGroups}
                onChange={(e) => setDomainAdminGroups(e.target.value)}
                placeholder="group-object-id-1, group-object-id-2"
              />
            </div>
            <div>
              <label htmlFor="settings-user-groups" className="mb-1 block text-sm font-medium">
                User Groups
              </label>
              <Input
                id="settings-user-groups"
                value={userGroups}
                onChange={(e) => setUserGroups(e.target.value)}
                placeholder="group-object-id-1, group-object-id-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* SIEM Integration Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">SIEM Integration</CardTitle>
            <CardDescription>
              Configure Blumira SIEM endpoint and authentication.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="settings-blumira-endpoint" className="mb-1 block text-sm font-medium">
                Blumira Endpoint URL
              </label>
              <Input
                id="settings-blumira-endpoint"
                value={blumiraEndpoint}
                onChange={(e) => setBlumiraEndpoint(e.target.value)}
                placeholder="https://api.blumira.com/v1/events"
              />
            </div>
            <div>
              <label htmlFor="settings-blumira-arn" className="mb-1 block text-sm font-medium">
                Blumira API Key Secret ARN
              </label>
              <Input
                id="settings-blumira-arn"
                value={blumiraApiKeySecretArn}
                onChange={(e) => setBlumiraApiKeySecretArn(e.target.value)}
                placeholder="arn:aws:secretsmanager:us-east-1:123456789:secret:blumira-key"
              />
            </div>
          </CardContent>
        </Card>

        {/* API Key Policy Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">API Key Policy</CardTitle>
            <CardDescription>
              Configure API key lifetime and capacity limits.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="settings-max-lifetime" className="mb-1 block text-sm font-medium">
                Max Lifetime
              </label>
              <select
                id="settings-max-lifetime"
                value={maxLifetimeMs}
                onChange={(e) => setMaxLifetimeMs(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Maximum API key lifetime"
              >
                {LIFETIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                <option value={UNLIMITED_LIFETIME_MS}>No limit (allows no-expiry keys)</option>
              </select>
            </div>
            <div>
              <label htmlFor="settings-default-lifetime" className="mb-1 block text-sm font-medium">
                Default Lifetime
              </label>
              <select
                id="settings-default-lifetime"
                value={defaultLifetimeMs}
                onChange={(e) => setDefaultLifetimeMs(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Default API key lifetime"
              >
                {LIFETIME_OPTIONS.filter((opt) => maxLifetimeMs === UNLIMITED_LIFETIME_MS || opt.value <= maxLifetimeMs).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="settings-max-keys" className="mb-1 block text-sm font-medium">
                Max Keys Per Subdomain
              </label>
              <Input
                id="settings-max-keys"
                type="number"
                min={1}
                max={100}
                value={maxKeysPerSubdomain}
                onChange={(e) => setMaxKeysPerSubdomain(Number(e.target.value))}
                aria-describedby="settings-max-keys-help"
              />
              <p id="settings-max-keys-help" className="mt-1 text-xs text-muted-foreground">
                Maximum active API keys allowed per subdomain per user.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Health Section (placeholder) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">System Health</CardTitle>
            <CardDescription>
              System health metrics and status (coming soon).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Health monitoring and metrics will be available in a future release.
            </p>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex items-center gap-3">
          <Button type="submit" loading={state.isSaving}>
            Save Settings
          </Button>
          {state.settings && (
            <span className="text-xs text-muted-foreground">
              Last updated: {new Date(state.settings.updatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

// ─── Exported Page Component ─────────────────────────────────────────────────

/**
 * SettingsPage wraps the inner component with SettingsProvider.
 * System_Admin only — access controlled within the page.
 */
export function SettingsPage(): React.JSX.Element {
  return (
    <SettingsProvider>
      <SettingsPageInner />
    </SettingsProvider>
  );
}
